import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { Box, CircularProgress, Tooltip } from "@mui/material";
import { HrefIconButton } from "../lib/common";
import { PhotoAdminTabs, PhotoQueueItemStates, PhotoSessionStates } from "../config/PhotoConfigs.js";
import { usePhotoUploadSession } from "../lib/hooks";
import FormatUtils from "../utils/FormatUtils.js";

/**
 * @param {number} count
 * @returns {string}
 */
function failedCountLabel(count) {
  if (count === 1) {
    return "1 chyba";
  }
  const formatted = FormatUtils.formatNumber(count, { maxDecimals: 0 });
  if (count >= 2 && count <= 4) {
    return `${formatted} chyby`;
  }
  return `${formatted} chyb`;
}

/**
 * Queue progress for the current upload run (success + fail + in-queue skip).
 *
 * @param {{ items?: object[], uploadedCount?: number, totalCount?: number }} session
 * @returns {{ done: number, total: number, percent: number, failedCount: number }}
 */
function getUploadQueueProgress(session) {
  const total = session.totalCount ?? 0;
  const items = session.items ?? [];
  const failedCount = items.filter((item) => item.state === PhotoQueueItemStates.FAILED).length;
  const skippedInQueue = items.filter((item) => item.state === PhotoQueueItemStates.SKIPPED && !item.skipReason).length;
  const done = Math.min(total, (session.uploadedCount ?? 0) + failedCount + skippedInQueue);
  const percent = total > 0 ? Math.round((done / total) * 100) : 0;
  return { done, total, percent, failedCount };
}

/**
 * Top-bar shortcut back to the upload tab while a queue is running or finished with failures.
 */
function PhotoUploadStatusButton() {
  const session = usePhotoUploadSession();
  const running = session.state === PhotoSessionStates.RUNNING;
  const { done, total, percent, failedCount } = getUploadQueueProgress(session);
  const hasFailed = failedCount > 0;
  const visible =
    running ||
    ((session.state === PhotoSessionStates.DONE || session.state === PhotoSessionStates.CANCELLED) && hasFailed);

  if (!visible) {
    return null;
  }

  const params = new URLSearchParams();
  if (session.raceId) {
    params.set("id", session.raceId);
  }
  params.set("tab", PhotoAdminTabs.UPLOAD);
  const href = `/photos?${params.toString()}`;

  const doneLabel = FormatUtils.formatNumber(done, { maxDecimals: 0 });
  const totalLabel = FormatUtils.formatNumber(total, { maxDecimals: 0 });
  const percentLabel = FormatUtils.formatNumber(percent, { maxDecimals: 0 });
  const countSummary = `${doneLabel} z ${totalLabel} nahráno (${percentLabel} %)`;
  let summary = countSummary;
  if (!running && session.state === PhotoSessionStates.CANCELLED) {
    summary = `Nahrávání zrušeno · ${failedCountLabel(failedCount)}`;
  } else if (!running) {
    summary = `Nahrávání dokončeno · ${failedCountLabel(failedCount)}`;
  }

  const tooltipTitle = session.currentFilename ? `${summary}\n${session.currentFilename}` : summary;

  return (
    <Tooltip title={tooltipTitle} describeChild slotProps={{ tooltip: { sx: { whiteSpace: "pre-line" } } }}>
      <HrefIconButton href={href} color="inherit" aria-label={summary}>
        <Box sx={{ position: "relative", display: "inline-flex", width: 32, height: 32 }}>
          <CircularProgress
            variant="determinate"
            value={100}
            size={32}
            thickness={3}
            sx={{ position: "absolute", left: 0, top: 0, color: "inherit", opacity: 0.28 }}
            aria-hidden
          />
          <CircularProgress
            variant="determinate"
            value={running ? percent : 100}
            size={32}
            thickness={3}
            color={hasFailed && !running ? "error" : "inherit"}
            sx={{ position: "absolute", left: 0, top: 0 }}
            aria-hidden
          />
          <Box sx={{ width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <CloudUploadIcon fontSize="small" />
          </Box>
        </Box>
      </HrefIconButton>
    </Tooltip>
  );
}

export default PhotoUploadStatusButton;
